import React from "react";

/**
 * Public legal page — SMS / TCPA consent.
 * Boilerplate covers Twilio's opt-in / HELP / STOP requirements and the
 * use-cases this app actually sends (delivery status + driver ETA).
 * Replace COMPANY_* placeholders before going live.
 */
const COMPANY = "Santa Rosa Commercial Investment, Inc. (SRCI Pharm-Route)";
const SUPPORT_EMAIL = "santarosacommercial@gmail.com";
const SUPPORT_PHONE = "(786) 000-0000"; // TODO update with real number

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-slate-900 mb-2">{title}</h2>
      <div className="text-sm text-slate-700 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function SmsConsent() {
  return (
    <div data-testid="page-sms-consent" className="min-h-screen bg-slate-50 px-6 py-12">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
        <a href="/" className="text-xs text-primary font-medium mb-6 inline-block">&larr; Back to sign-in</a>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">SMS Communications Consent</h1>
        <p className="text-xs text-slate-500 mb-8">Last updated: June 2026</p>

        <Section title="1. Program description">
          <p>
            {COMPANY} ("we", "us", "our") sends SMS messages to patients and authorized contacts
            for the sole purpose of communicating prescription delivery status — including
            tracking links, driver ETA notifications, delivery confirmations, and exception alerts
            (e.g. failed delivery, return to pharmacy).
          </p>
          <p>We do <strong>not</strong> use SMS for marketing or promotional content.</p>
        </Section>

        <Section title="2. How you opt in">
          <p>
            By providing your mobile phone number to a participating pharmacy or to our delivery
            staff, you expressly consent to receive these transactional SMS messages from us at
            the number provided. Message frequency varies based on your delivery activity
            (typically 1-4 messages per delivery).
          </p>
        </Section>

        <Section title="3. Message and data rates">
          <p>
            Standard message and data rates apply per your wireless carrier plan. We do not
            charge a fee for sending or receiving these messages.
          </p>
        </Section>

        <Section title="4. Opt out — text STOP at any time">
          <p>
            Reply <strong>STOP</strong> to any SMS we send to immediately opt out of all future
            messages from {COMPANY}. You will receive one confirmation message and no further
            messages thereafter. To re-subscribe, reply <strong>START</strong> or contact your
            pharmacy.
          </p>
          <p>
            Reply <strong>HELP</strong> for help, or contact us directly at{" "}
            <a className="text-primary underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{" "}
            or {SUPPORT_PHONE}.
          </p>
        </Section>

        <Section title="5. Privacy">
          <p>
            Phone numbers and SMS message content are stored only as long as needed to deliver
            the message and provide customer support. They are never sold, rented, or shared
            with third parties for marketing purposes. See our{" "}
            <a className="text-primary underline" href="/privacy-policy">Privacy Policy</a> for
            full details, including HIPAA-related handling.
          </p>
        </Section>

        <Section title="6. Carriers supported">
          <p>
            Supported carriers include AT&amp;T, T-Mobile, Verizon Wireless, Sprint, and most
            other US carriers. We are not liable for delayed or undelivered messages caused by
            the carrier.
          </p>
        </Section>

        <Section title="7. Contact">
          <p>
            Questions about this SMS program? Email{" "}
            <a className="text-primary underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </Section>

        <p className="text-xs text-slate-400 mt-8 italic">
          This is a starter template. Have your legal counsel review and customize before going live.
        </p>
      </div>
    </div>
  );
}
